import React, { useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import CustomDialog from "../../../components/common/CustomDialog";
import CustomSelect from "../../../components/common/CustomSelect";
import CustomTextInput from "../../../components/common/CustomTextInput";
import CustomCheckbox from "../../../components/common/CustomCheckbox";
import CustomButton from "../../../components/common/CustomButton/CustomButton";
import Grid from "../../../components/layout/grid/Grid";
import { X } from "lucide-react";
import { addNotificationTriggerMessagesUrl, getNotificationTriggerListUrl, getNotificationTriggerMessagesUrl, updateNotificationTriggerMessagesUrl } from "../../../utils/urls";
import { errorToast, successToast } from "../../../utils/toasts";
import axios from "axios";
import SmsEditor from "../../../components/common/SmsEditor/SmsEditor";

const validationSchema = Yup.object().shape({
  notification_trigger_list_id: Yup.string().required("Trigger is required"),
  recipient: Yup.string().required("Recipient is required"),
  title: Yup.string().required("Title is required"),

  is_sms: Yup.boolean(),
  is_email: Yup.boolean(),
  is_whatsapp: Yup.boolean(),
  is_active: Yup.boolean(),

  email_subject: Yup.string().when("is_email", {
    is: true,
    then: schema => schema.required("Email subject is required"),
    otherwise: schema => schema.optional()
  }),

  email_message: Yup.string().when("is_email", {
    is: true,
    then: schema => schema.required("Email message is required"),
    otherwise: schema => schema.optional()
  }),

  whatsapp_message: Yup.string().when("is_whatsapp", {
    is: true,
    then: schema => schema.required("WhatsApp message is required"),
    otherwise: schema => schema.optional()
  }),

  sms_message: Yup.string().when("is_sms", {
    is: true,
    then: schema => schema.required("SMS message is required"),
    otherwise: schema => schema.optional()
  })
}).test(
  "atLeastOneChannel",
  null,
  function (values) {
    const { is_sms, is_email, is_whatsapp } = values;
    if (!is_sms && !is_email && !is_whatsapp) {
      return this.createError({
        path: "atLeastOneChannel",
        message:
          "At least one channel (SMS, Email, or WhatsApp) must be selected"
      });
    }

    return true;
  }
);


const NotificationFormDialog = ({ open, onClose, onSubmit, editData = null }) => {
  const initialValues = {
    notification_trigger_list_id: "",
    recipient: "",
    title: "",
    is_sms: true,
    is_email: true,
    is_whatsapp: true,
    email_subject: "",
    email_message: "",
    whatsapp_message: "",
    sms_message: "",
    is_active: true
  };

  const [notificationTriggerList, setNotificationTriggerList] = useState([]);
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (notificationTriggerList?.length === 0) {
      fetchNotificationTriggerList();
    }
  }, [])
  
  function fetchNotificationTriggerList() {
    axios.get(getNotificationTriggerListUrl)
      .then(response => {
        setNotificationTriggerList(response?.data?.data);
        if (editData) {
          setSelectedTrigger(response?.data?.data?.find(trigger => trigger.id === editData?.notification_trigger_list_id?.id));
        }
      })
      .catch(error => {
        errorToast(error.message);
      });
  }

  const handleSubmit = (values) => {
    const submitData = {
      ...values
    }
    
    setIsLoading(true);

    if (editData?.id) {
      submitData.id = editData?.id;

      axios.put(updateNotificationTriggerMessagesUrl, submitData)
      .then(response => {
          console.log("response", response);
          successToast("Notification Updated Successfully!");
          onSubmit();
          onClose();
          setIsLoading(false);
        })
        .catch(error => {
          setIsLoading(false);
          console.error(error.response);
          errorToast(error?.response?.data?.error?.message || "Failed to update notification. Please try again.");
        });
    } else {
      axios.post(addNotificationTriggerMessagesUrl, submitData)
      .then(response => {
          console.log("response", response);
          successToast("Notification Added Successfully!");
          onSubmit();
          onClose();
          setIsLoading(false);
        })
        .catch(error => {
          setIsLoading(false);
          console.error(error.response);
          errorToast(error?.response?.data?.error?.message || "Failed to add notification. Please try again.");
        });
    }
  };

  const getInitialValues = () => {
    if (editData) {
      return {
        notification_trigger_list_id: editData?.notification_trigger_list_id?.id,
        recipient: editData?.recipient,
        title: editData?.title,
        is_sms: editData?.is_sms,
        is_email: editData?.is_email,
        is_whatsapp: editData?.is_whatsapp,
        email_subject: editData?.email_subject,
        email_message: editData?.email_message,
        whatsapp_message: editData?.whatsapp_message,
        sms_message: editData?.sms_message,
        is_active: editData?.is_active,
      };
    }
    return initialValues;
  };

  return (
    <CustomDialog
      dontCloseOnOverlay={true}
      open={open}
      onClose={onClose}
      title=""
      size="md"
      className="!p-0 sm:!max-w-[500px] md:!max-w-[600px] lg:!max-w-[800px]"
    >
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, errors }) => {
          console.log("errors", errors);
          return (
            <Form>
              {/* Header */}
              <div className="flex justify-between items-center bg-[#0051AD] px-4 py-3 rounded-t-lg">
                <span className="text-sm font-medium text-white">New Notification</span>
                <button 
                  type="button"
                  onClick={onClose}
                  className="text-white hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form Content */}
              <div className="p-6">
                <Grid gap={24}>
                  <Grid item xs={12}>
                    <label className="text-gray-700 font-medium mb-2 block">Trigger</label>
                    <CustomSelect
                      name="notification_trigger_list_id"
                      placeholder="Select"
                      menuList={notificationTriggerList?.map((item) => ({ value: item.id, label: item.title }))}
                      onChange={(_e) => {
                        setFieldValue("recipient", "");
                        setSelectedTrigger(notificationTriggerList.find((item) => item.id === _e?.target?.value));
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <label className="text-gray-700 font-medium mb-2 block">Recipients</label>
                    <CustomSelect
                      name="recipient"
                      placeholder="Select"
                      disabled={!selectedTrigger}
                      menuList={selectedTrigger?.recipient?.map((item) => ({ value: item, label: item }))}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <label className="text-gray-700 font-medium mb-2 block">Title</label>
                    <CustomTextInput
                      name="title"
                      placeholder="Enter"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <label className="text-gray-700 font-medium mb-2 block">Delivery method</label>
                    <div className="flex gap-6">
                      <CustomCheckbox
                        label="SMS"
                        checked={values.is_sms}
                        onChange={(_e) => setFieldValue('is_sms', _e?.target?.value)}
                        className="!checked:bg-primary"
                      />
                      <CustomCheckbox
                        label="Whatsapp"
                        checked={values.is_whatsapp}
                        onChange={(_e) => setFieldValue('is_whatsapp', _e?.target?.value)}
                      />
                      <CustomCheckbox
                        label="Email"
                        checked={values.is_email}
                        onChange={(_e) => setFieldValue('is_email', _e?.target?.value)}
                      />
                    </div>
                    <p className="text-error mt-2">{errors?.atLeastOneChannel}</p>
                  </Grid>
                </Grid>

                <Grid item xs={12} className={"mt-6"}>
                  <label className="text-gray-700 font-medium mb-2 block">SMS Message Content</label>
                  <SmsEditor
                    // ref={editorRef}
                    disabled={!values.is_sms}
                    value={values.sms_message}
                    variables={selectedTrigger?.variable_list}
                    placeholder="Type your SMS content..."
                    onChange={(val) => {
                      console.log(val);
                      setFieldValue("sms_message", val)
                    }}
                  />
                  <p className="text-error">{errors["sms_message"]}</p>
                </Grid>

                <Grid item xs={12} className={"mt-6"}>
                  <label className="text-gray-700 font-medium mb-2 block">Whats App Message Content</label>
                  <SmsEditor
                    // ref={editorRef}
                    disabled={!values.is_whatsapp}
                    value={values.whatsapp_message}
                    variables={selectedTrigger?.variable_list}
                    placeholder="Type your Whatsapp content..."
                    onChange={(val) => {
                      console.log(val);
                      setFieldValue("whatsapp_message", val)
                    }}
                  />
                </Grid>

                <Grid item xs={12} className={"mt-6"}>
                  <label className="text-gray-700 font-medium mb-2 block">Email Message Content</label>
                  <CustomTextInput
                    disable={!values.is_email}
                    name="email_subject"
                    placeholder="Enter Subject"
                  />
                  <div className="mt-2">
                    <SmsEditor
                      // ref={editorRef}
                      value={values.email_message}
                      disabled={!values.is_email}
                      variables={selectedTrigger?.variable_list}
                      placeholder="Type your email content..."
                      onChange={(val) => {
                        console.log(val);
                        setFieldValue("email_message", val)
                      }}
                    />
                  </div>
                </Grid>

                <div className="flex justify-end gap-3 mt-6">
                  <CustomButton
                    label="Cancel"
                    variant="outlined"
                    onClick={onClose}
                    disabled={isLoading}
                    type="button"
                  />
                  <CustomButton
                    label="Save"
                    variant="contained"
                    type="submit"
                    isLoading={isLoading}
                  />
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    </CustomDialog>
  );
};

export default NotificationFormDialog; 